import type { CampaignAnalyticsGranularity, CampaignAnalyticsKpi } from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconChartLine,
  IconChevronDown,
  IconChevronUp,
  IconDatabaseOff,
} from "@tabler/icons-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Area, Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  countMarkersInPeriod,
  formatTrendPeriod,
  hasIncompleteTrendData,
  PERFORMANCE_GRANULARITIES,
  type PerformanceKpi,
  type PerformanceMetric,
  type PerformanceTrendMarkers,
  type PerformanceTrendPoint,
  parsePerformanceSlots,
  resolveSelectedMetric,
  setPerformanceSlot,
} from "./performance";
import { type Formatters, formatKpiValue } from "./performance-format";
import { KpiCard } from "./performance-kpi-card";
import { PerformanceSkeleton } from "./performance-skeleton";

/**
 * KPI slot row and trend chart; an optional comparison metric enables a second series.
 * The default metric type and single-series behavior preserve Campaign/Portfolio callers. Campaign and Portfolio pages wrap this with their own metric set,
 * storage keys, labels, and empty states; all shared performance strings live
 * in the `campaign` i18n namespace.
 *
 * Menu labels are shorter than the KPI labels the API sends ("Ad Sales" reads
 * as "Sales" in a metric picker) and stay stable even if the API relabels a
 * metric. They resolve at render via `t("performance.metricMenu.<metric>")`
 * unless the wrapper supplies `getMetricLabel`.
 */
export interface PerformancePanelProps<M extends string = PerformanceMetric> {
  /** Metrics offered by the slot menus, in menu order. */
  metrics: readonly M[];
  /** Card row shown before the user customizes slots; fixes the row length. */
  defaultSlots: readonly M[];
  kpis: PerformanceKpi<M>[];
  trend: PerformanceTrendPoint<M>[];
  dataThrough: string | null;
  metric: M;
  granularity: CampaignAnalyticsGranularity;
  onMetricChange: (metric: M) => void;
  onGranularityChange: (granularity: CampaignAnalyticsGranularity) => void;
  isLoading: boolean;
  isFetching: boolean;
  /** Independent data sources may keep already-loaded metrics interactive. */
  disableInteractionsWhileFetching?: boolean;
  isError: boolean;
  onRetry: () => void;
  /** localStorage keys, per page, so collapse/slot choices persist independently. */
  collapseStorageKey: string;
  slotsStorageKey: string;
  /** Replaces the KPI row + chart (e.g. campaign disconnected / no-match states). */
  emptyState?: React.ReactNode;
  /** Localized metric name; falls back to the API label / metric-menu strings. */
  getMetricLabel?: (key: M) => string;
  /** Compact preserves dense list-page typography; default follows the product type scale. */
  size?: "compact" | "default";
  /** Shows the previous reporting period value alongside its change. */
  showPreviousValue?: boolean;
  /** Pages with a dedicated freshness indicator can keep the chart concise. */
  showIncompleteNotice?: boolean;
  compareMetric?: M | null;
  onCompareMetricChange?: (metric: M | null) => void;
  title?: string;
  subtitle?: string;
  notice?: React.ReactNode;
  /** Events counted per bucket and marked on the chart (e.g. 操作日志 changes). */
  markers?: PerformanceTrendMarkers;
}

export function PerformancePanel<M extends string = PerformanceMetric>({
  metrics,
  defaultSlots,
  kpis,
  trend,
  dataThrough,
  metric,
  granularity,
  onMetricChange,
  onGranularityChange,
  isLoading,
  isFetching,
  disableInteractionsWhileFetching = true,
  isError,
  onRetry,
  collapseStorageKey,
  slotsStorageKey,
  emptyState,
  getMetricLabel,
  size = "compact",
  showPreviousValue = false,
  showIncompleteNotice = true,
  compareMetric = null,
  onCompareMetricChange,
  title,
  subtitle,
  notice,
  markers,
}: PerformancePanelProps<M>): React.ReactElement {
  const { t } = useTranslation("campaign");
  const contentId = useId();
  const [expanded, setExpanded] = useState(() => {
    return readPreference(collapseStorageKey) !== "collapsed";
  });
  const [slots, setSlots] = useState<M[]>(() =>
    parsePerformanceSlots(readPreference(slotsStorageKey), metrics, defaultSlots),
  );

  useEffect(() => {
    writePreference(collapseStorageKey, expanded ? "expanded" : "collapsed");
  }, [collapseStorageKey, expanded]);

  useEffect(() => {
    writePreference(slotsStorageKey, JSON.stringify(slots));
  }, [slotsStorageKey, slots]);

  // The charted metric lives in the URL, so it can point at a metric this row no
  // longer shows (shared link, or a slot the user just swapped out).
  const selectedMetric = resolveSelectedMetric(slots, metric);
  const hasContent = kpis.length > 0 || emptyState != null;

  const handleSlotChange = (index: number, next: M) => {
    const updated = setPerformanceSlot(slots, index, next);
    // Rejected (the metric is already on the row), so the chart selection must
    // not move to a metric no card is showing.
    if (updated[index] !== next) return;
    setSlots(updated);
    // Keep the chart on the card the user just edited.
    if (slots[index] === selectedMetric) onMetricChange(next);
    if (slots[index] === compareMetric) onCompareMetricChange?.(next);
  };

  // Keep the chart dimensions stable as the page frame grows beyond the
  // viewport to accommodate the paginated list below it.
  return (
    <section
      aria-busy={isFetching}
      data-slot="performance-panel"
      data-size={size}
      className="w-full min-w-0 shrink-0 overflow-hidden rounded-[14px] border border-line bg-surface"
    >
      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 px-4 py-2.5",
          size === "default" ? "min-h-16" : "min-h-[54px]",
        )}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <IconChartLine className="size-4 text-primary" aria-hidden />
            <h2
              className={cn(
                "font-semibold text-ink",
                size === "default" ? "text-base leading-snug" : "text-[14px]",
              )}
            >
              {title ?? t("performance.title")}
            </h2>
            {isFetching && !isLoading && hasContent ? (
              <span
                className={cn(
                  "text-muted-foreground",
                  size === "default" ? "text-xs" : "text-[9px]",
                )}
                aria-live="polite"
              >
                {t("performance.updating")}
              </span>
            ) : null}
          </div>
          <p
            className={cn(
              "mt-0.5 truncate text-muted-foreground",
              size === "default" ? "text-xs leading-4" : "text-[9px]",
            )}
          >
            {subtitle ??
              (dataThrough
                ? t("performance.dataThrough", { date: dataThrough })
                : t("performance.attributionPeriod"))}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {expanded ? (
            <GranularityControl
              value={granularity}
              onChange={onGranularityChange}
              size={size}
              showHour={!onCompareMetricChange}
            />
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className={cn(
              "h-8 rounded-[8px] px-2.5",
              size === "default" ? "text-xs" : "text-[10px]",
            )}
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? t("performance.collapse") : t("performance.expand")}
            {expanded ? (
              <IconChevronUp data-icon="inline-end" aria-hidden />
            ) : (
              <IconChevronDown data-icon="inline-end" aria-hidden />
            )}
          </Button>
        </div>
      </div>

      {expanded ? (
        <div id={contentId} className="border-t border-line">
          {isLoading ? <PerformanceSkeleton defaultSlots={defaultSlots} size={size} /> : null}
          {!isLoading && isError && !hasContent ? (
            <div className="min-h-[290px]">
              <ErrorState message={t("performance.loadError")} onRetry={onRetry} />
            </div>
          ) : null}
          {/* A caller can legitimately hand over an empty KPI list (no data yet,
              no error); fall back to the no-data block rather than a bare body. */}
          {!isLoading && !isError && !hasContent ? (
            <PerformancePanelEmptyState
              title={t("performance.noDataTitle")}
              description={t("performance.noDataDescription")}
              size={size}
            />
          ) : null}
          {!isLoading && hasContent ? (
            <div
              className={cn(
                "transition-opacity duration-150",
                isFetching && disableInteractionsWhileFetching && "pointer-events-none opacity-55",
              )}
            >
              {isError ? (
                <div
                  className={cn(
                    "flex items-center justify-between gap-4 border-b border-destructive/20 bg-destructive/5 px-4 py-2 text-destructive-foreground",
                    size === "default" ? "text-sm" : "text-[10px]",
                  )}
                >
                  <span>{t("performance.refreshFailed")}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    className="h-6 text-destructive-foreground"
                    onClick={onRetry}
                  >
                    {t("common:actions.retry")}
                  </Button>
                </div>
              ) : null}
              {emptyState ?? (
                <PerformanceContent
                  showPreviousValue={showPreviousValue}
                  showIncompleteNotice={showIncompleteNotice}
                  metrics={metrics}
                  kpis={kpis}
                  trend={trend}
                  dataThrough={dataThrough}
                  metric={selectedMetric}
                  compareMetric={
                    compareMetric &&
                    slots.includes(compareMetric) &&
                    compareMetric !== selectedMetric
                      ? compareMetric
                      : null
                  }
                  onCompareMetricChange={onCompareMetricChange}
                  markers={markers}
                  slots={slots}
                  onMetricChange={onMetricChange}
                  onSlotChange={handleSlotChange}
                  getMetricLabel={getMetricLabel}
                  size={size}
                />
              )}
            </div>
          ) : null}
          {notice ? <div className="border-t border-line px-4 py-3">{notice}</div> : null}
        </div>
      ) : null}
    </section>
  );
}

/** Icon + title + description block for wrapper-supplied `emptyState`s. */
export function PerformancePanelEmptyState({
  title,
  description,
  size = "compact",
}: {
  title: string;
  description: string;
  size?: "compact" | "default";
}): React.ReactElement {
  return (
    <div className="flex min-h-[290px] flex-col items-center justify-center px-6 text-center">
      <IconDatabaseOff className="size-10 text-muted-foreground/50" aria-hidden />
      <p
        className={cn(
          "mt-3 font-semibold text-ink",
          size === "default" ? "text-base leading-6" : "text-[13px]",
        )}
      >
        {title}
      </p>
      <p
        className={cn(
          "mt-1 max-w-md text-muted-foreground",
          size === "default" ? "text-sm leading-5" : "text-[11px] leading-4",
        )}
      >
        {description}
      </p>
    </div>
  );
}

function GranularityControl({
  value,
  onChange,
  size,
  showHour,
}: {
  value: CampaignAnalyticsGranularity;
  onChange: (value: CampaignAnalyticsGranularity) => void;
  showHour: boolean;
  size: "compact" | "default";
}) {
  const { t } = useTranslation("campaign");
  const hourUnavailableId = useId();
  return (
    <fieldset className="flex h-8 items-center rounded-[8px] bg-subtle p-0.5">
      <legend className="sr-only">{t("performance.granularityLegend")}</legend>
      {showHour ? (
        <>
          <button
            type="button"
            aria-disabled="true"
            aria-describedby={hourUnavailableId}
            className={cn(
              "h-7 cursor-not-allowed rounded-[6px] px-2.5 text-muted-foreground/45",
              size === "default" ? "text-xs" : "text-[10px]",
            )}
          >
            {t("performance.hour")}
          </button>
          <span id={hourUnavailableId} className="sr-only">
            {t("performance.hourUnavailable")}
          </span>
        </>
      ) : null}
      {PERFORMANCE_GRANULARITIES.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          className={cn(
            "h-7 rounded-[6px] px-2.5 font-medium capitalize text-muted-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
            size === "default" ? "text-xs" : "text-[10px]",
            value === option && "bg-surface text-ink shadow-xs",
          )}
          onClick={() => onChange(option)}
        >
          {t(`performance.granularity.${option}`)}
        </button>
      ))}
    </fieldset>
  );
}

function PerformanceContent<M extends string>({
  showPreviousValue,
  showIncompleteNotice,
  metrics,
  kpis,
  trend,
  dataThrough,
  metric,
  compareMetric,
  onCompareMetricChange,
  slots,
  onMetricChange,
  onSlotChange,
  getMetricLabel,
  size,
  markers,
}: {
  markers?: PerformanceTrendMarkers;
  showPreviousValue: boolean;
  showIncompleteNotice: boolean;
  metrics: readonly M[];
  kpis: PerformanceKpi<M>[];
  trend: PerformanceTrendPoint<M>[];
  dataThrough: string | null;
  metric: M;
  compareMetric: M | null;
  onCompareMetricChange?: (metric: M | null) => void;
  slots: readonly M[];
  onMetricChange: (metric: M) => void;
  onSlotChange: (index: number, metric: M) => void;
  getMetricLabel?: (key: M) => string;
  size: "compact" | "default";
}) {
  const { t } = useTranslation("campaign");
  const kpiMap = new Map(kpis.map((kpi) => [kpi.key, kpi]));
  const selectedKpi = kpiMap.get(metric) ?? kpis[0];
  const comparisonKpi = compareMetric ? kpiMap.get(compareMetric) : undefined;
  const hasTrendData = trend.some(
    (point) =>
      (selectedKpi && point[selectedKpi.key] != null) ||
      (comparisonKpi && point[comparisonKpi.key] != null),
  );
  const selectMetric = (next: M) => {
    if (!onCompareMetricChange) {
      onMetricChange(next);
      return;
    }
    if (next === compareMetric) {
      onCompareMetricChange(null);
      return;
    }
    if (next === metric) {
      if (compareMetric) {
        onMetricChange(compareMetric);
        onCompareMetricChange(null);
      }
      return;
    }
    onCompareMetricChange(next);
  };
  const selectedMetrics = [selectedKpi, comparisonKpi].filter((item) => item != null);
  const hasIncompleteData = selectedMetrics.some((item) =>
    hasIncompleteTrendData(
      trend.map((point) => ({
        ...point,
        dataStatus: point.metricDataStatus?.[item.key] ?? point.dataStatus,
      })),
      dataThrough,
    ),
  );

  return (
    <>
      <div className="grid grid-cols-3 border-b border-line min-[1440px]:grid-cols-6">
        {slots.map((key, index) => {
          const kpi = kpiMap.get(key);
          const borders = cn(
            index % 3 !== 2 && "max-[1439px]:border-r",
            index < slots.length - 3 && "max-[1439px]:border-b",
            index % 6 !== 5 && index < slots.length - 1 && "min-[1440px]:border-r",
          );
          return kpi ? (
            <KpiCard
              key={key}
              kpi={kpi}
              showPreviousValue={showPreviousValue}
              selected={selectedMetrics.some((item) => item.key === key)}
              selectionColor={
                onCompareMetricChange
                  ? selectedKpi?.key === key
                    ? "var(--sales-chart-primary, #679bff)"
                    : comparisonKpi?.key === key
                      ? "var(--sales-chart-comparison, #ff998a)"
                      : null
                  : undefined
              }
              slotIndex={index}
              metrics={metrics}
              takenMetrics={slots}
              onClick={() => selectMetric(key)}
              onSlotChange={onSlotChange}
              getMetricLabel={getMetricLabel}
              size={size}
              className={borders}
            />
          ) : (
            <div
              key={key}
              className={cn(size === "default" ? "h-28" : "h-[94px]", "border-line", borders)}
            />
          );
        })}
      </div>

      {showIncompleteNotice && hasIncompleteData ? (
        <div
          className={cn(
            "flex items-center gap-2 border-b border-warning/20 bg-warning/5 px-4 py-2 text-warning-foreground",
            size === "default" ? "text-sm" : "text-[10px]",
          )}
        >
          <IconAlertTriangle className="size-3.5" aria-hidden />
          {t("performance.incompleteData")}
        </div>
      ) : null}

      {selectedKpi && hasTrendData ? (
        <PerformanceTrendChart
          kpi={selectedKpi}
          comparisonKpi={comparisonKpi}
          dualMode={!!onCompareMetricChange}
          trend={trend}
          getMetricLabel={getMetricLabel}
          size={size}
          markers={markers}
        />
      ) : (
        <div className="flex h-[250px] flex-col items-center justify-center px-6 text-center">
          <IconDatabaseOff className="size-9 text-muted-foreground/45" aria-hidden />
          <p
            className={cn(
              "mt-3 font-semibold text-ink",
              size === "default" ? "text-base" : "text-[12px]",
            )}
          >
            {t("performance.noDataTitle")}
          </p>
          <p
            className={cn(
              "mt-1 text-muted-foreground",
              size === "default" ? "text-sm" : "text-[10px]",
            )}
          >
            {t("performance.noDataDescription")}
          </p>
        </div>
      )}
    </>
  );
}

function PerformanceTrendChart<M extends string>({
  kpi,
  comparisonKpi,
  dualMode,
  trend,
  getMetricLabel,
  size,
  markers,
}: {
  kpi: PerformanceKpi<M>;
  comparisonKpi?: PerformanceKpi<M>;
  dualMode: boolean;
  trend: PerformanceTrendPoint<M>[];
  getMetricLabel?: (key: M) => string;
  size: "compact" | "default";
  markers?: PerformanceTrendMarkers;
}) {
  const { t } = useTranslation("campaign");
  const { locale, formatCurrency, formatNumber, formatPercent } = useFormatters();
  const formatters = { formatCurrency, formatNumber, formatPercent };
  const label = getMetricLabel?.(kpi.key) ?? kpi.label;
  const comparisonLabel = comparisonKpi
    ? (getMetricLabel?.(comparisonKpi.key) ?? comparisonKpi.label)
    : undefined;
  const combinedLabel = [label, comparisonLabel].filter(Boolean).join(" / ");
  const chartConfig = {
    [kpi.key]: {
      label,
      color: dualMode ? "var(--sales-chart-primary, #679bff)" : "var(--color-chart-4)",
    },
    ...(comparisonKpi
      ? {
          [comparisonKpi.key]: {
            label: comparisonLabel,
            color: "var(--sales-chart-comparison, #ff998a)",
          },
        }
      : {}),
  } satisfies ChartConfig;
  const data = trend.map((point) => {
    const markerCount = markers
      ? countMarkersInPeriod(markers.dailyCounts, point.periodStart, point.periodEnd)
      : 0;
    return {
      ...point,
      periodLabel: formatTrendPeriod(point.periodStart, point.periodEnd, locale),
      [MARKER_COUNT_KEY]: markerCount,
      // Pinned to the top of its own axis; the badge is drawn above it.
      [MARKER_Y_KEY]: markerCount > 0 ? 1 : null,
    };
  });

  return (
    <div className="min-w-0 px-4 pb-3 pt-4">
      {dualMode ? (
        <div className="mb-2 flex items-start justify-between gap-4 text-xs">
          <span style={{ color: "var(--sales-chart-primary)" }}>{label}</span>
          {comparisonKpi ? (
            <span style={{ color: "var(--sales-chart-comparison)" }}>{comparisonLabel}</span>
          ) : null}
        </div>
      ) : null}
      <div
        role="img"
        aria-label={t("performance.chartImageAria", {
          label: combinedLabel,
          buckets: trend.length,
        })}
      >
        <ChartContainer
          config={chartConfig}
          className="h-[320px] w-full min-w-0"
          initialDimension={{ width: 640, height: 320 }}
        >
          <ComposedChart
            data={data}
            // Markers sit in the top margin, clear of the tallest bar.
            margin={{ left: 4, right: 10, top: markers ? 26 : 6, bottom: 0 }}
          >
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="periodLabel"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={24}
              tick={{ fontSize: size === "default" ? 12 : 10 }}
            />
            <YAxis
              yAxisId="primary"
              tickLine={false}
              axisLine={false}
              width={58}
              tick={{ fontSize: size === "default" ? 12 : 10 }}
              tickFormatter={(value: number) =>
                formatAxisValue(Number(value), kpi.format, formatters, kpi.currency)
              }
            />
            {comparisonKpi ? (
              <YAxis
                yAxisId="comparison"
                orientation="right"
                tickLine={false}
                axisLine={false}
                width={64}
                tick={{
                  fontSize: size === "default" ? 12 : 10,
                  fill: "var(--sales-chart-comparison, #ff998a)",
                }}
                tickFormatter={(value: number) =>
                  formatAxisValue(
                    Number(value),
                    comparisonKpi.format,
                    formatters,
                    comparisonKpi.currency,
                  )
                }
              />
            ) : null}
            {markers ? <YAxis yAxisId="markers" hide domain={[0, 1]} /> : null}
            <ChartTooltip
              filterNull={false}
              cursor={{ fill: "var(--color-control-highlight)" }}
              content={
                <ChartTooltipContent
                  indicator="dot"
                  labelFormatter={(_label, payload) =>
                    String((payload[0]?.payload as { periodLabel?: string })?.periodLabel ?? "")
                  }
                  formatter={(value, name, item) => {
                    if (name === MARKER_Y_KEY) {
                      const count = Number(
                        (item.payload as Record<string, unknown>)[MARKER_COUNT_KEY] ?? 0,
                      );
                      return (
                        <span className="flex w-full items-center justify-between gap-4">
                          <span>{markers?.label}</span>
                          <span className="font-mono tabular-nums">{formatNumber(count)}</span>
                        </span>
                      );
                    }
                    const current = [kpi, comparisonKpi].find((item) => item?.key === name) ?? kpi;
                    return (
                      <span className="flex w-full items-center justify-between gap-4">
                        <span>{getMetricLabel?.(current.key) ?? current.label}</span>
                        <span className="font-mono tabular-nums">
                          {formatKpiValue(
                            value == null ? null : Number(value),
                            current.format,
                            formatters,
                            current.currency,
                          )}
                        </span>
                      </span>
                    );
                  }}
                />
              }
            />
            {/* A missing bucket is null, so Recharts draws no bar — the gap stays
                visible without the isolated-point handling a line chart needed. */}
            <Bar
              yAxisId="primary"
              dataKey={kpi.key}
              fill={`var(--color-${kpi.key})`}
              radius={[3, 3, 0, 0]}
              maxBarSize={48}
              isAnimationActive={false}
            />
            {comparisonKpi ? (
              <Area
                yAxisId="comparison"
                dataKey={comparisonKpi.key}
                type="monotone"
                stroke="var(--sales-chart-comparison, #ff998a)"
                fill="var(--sales-chart-comparison, #ff998a)"
                fillOpacity={0.12}
                strokeWidth={2}
                dot={{
                  r: 3,
                  fill: "var(--sales-chart-comparison, #ff998a)",
                  stroke: "var(--color-surface)",
                }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ) : null}
            {markers ? (
              <Line
                yAxisId="markers"
                dataKey={MARKER_Y_KEY}
                name={MARKER_Y_KEY}
                stroke="none"
                legendType="none"
                dot={<ChangeMarkerDot />}
                activeDot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ) : null}
          </ComposedChart>
        </ChartContainer>
      </div>

      <details
        className={cn(
          "mt-2 rounded-[8px] border border-line bg-subtle/40",
          size === "default" ? "text-sm" : "text-[10px]",
        )}
      >
        <summary className="cursor-pointer px-3 py-2 font-medium text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {t("performance.viewDataTable", { label: combinedLabel })}
        </summary>
        <div className="max-h-56 overflow-auto border-t border-line">
          <table className="w-full border-collapse tabular-nums">
            <caption className="sr-only">
              {t("performance.tableCaption", { label: combinedLabel })}
            </caption>
            <thead className="sticky top-0 bg-surface text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t("performance.tablePeriod")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {label}
                </th>
                {comparisonKpi ? (
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {comparisonLabel}
                  </th>
                ) : null}
                {markers ? (
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {markers.label}
                  </th>
                ) : null}
                {!dualMode ? (
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {t("performance.tableDataStatus")}
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {data.map((point) => (
                <tr
                  key={`${point.periodStart}-${point.periodEnd}`}
                  className="border-t border-line"
                >
                  <th scope="row" className="px-3 py-2 text-left font-normal text-ink">
                    {point.periodLabel}
                  </th>
                  <td className="px-3 py-2 text-right font-mono text-ink">
                    {formatKpiValue(point[kpi.key], kpi.format, formatters, kpi.currency)}
                  </td>
                  {comparisonKpi ? (
                    <td className="px-3 py-2 text-right font-mono text-ink">
                      {formatKpiValue(
                        point[comparisonKpi.key],
                        comparisonKpi.format,
                        formatters,
                        comparisonKpi.currency,
                      )}
                    </td>
                  ) : null}
                  {markers ? (
                    <td className="px-3 py-2 text-right font-mono text-ink">
                      {formatNumber(point[MARKER_COUNT_KEY])}
                    </td>
                  ) : null}
                  {!dualMode ? (
                    <td className="px-3 py-2 text-right capitalize text-muted-foreground">
                      {t(
                        `performance.dataStatus.${point.metricDataStatus?.[kpi.key] ?? point.dataStatus}`,
                      )}
                      {comparisonKpi &&
                      (point.metricDataStatus?.[comparisonKpi.key] ?? point.dataStatus) !==
                        (point.metricDataStatus?.[kpi.key] ?? point.dataStatus)
                        ? ` / ${t(`performance.dataStatus.${point.metricDataStatus?.[comparisonKpi.key] ?? point.dataStatus}`)}`
                        : ""}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

const MARKER_COUNT_KEY = "__markerCount";
const MARKER_Y_KEY = "__markerY";

/** A count badge above a bucket that has marked events. */
function ChangeMarkerDot(props: {
  cx?: number;
  cy?: number;
  value?: number | null;
  payload?: Record<string, unknown>;
}) {
  const { cx, cy, value, payload } = props;
  if (cx == null || cy == null || value == null) return null;
  const count = Number(payload?.[MARKER_COUNT_KEY] ?? 0);
  const text = count > 99 ? "99+" : String(count);
  const width = Math.max(16, text.length * 6 + 8);
  return (
    <g pointerEvents="none">
      <rect
        x={cx - width / 2}
        y={cy - 21}
        width={width}
        height={16}
        rx={8}
        fill="var(--color-primary)"
      />
      <text
        x={cx}
        y={cy - 9.5}
        textAnchor="middle"
        fontSize={10}
        fill="var(--color-primary-foreground)"
        className="tabular-nums"
      >
        {text}
      </text>
    </g>
  );
}

export function formatAxisValue(
  value: number,
  format: CampaignAnalyticsKpi["format"],
  formatters: Formatters,
  currency?: string | null,
): string {
  if (format === "currency" && currency === null) return "—";
  if (format === "percent") return formatters.formatPercent(value, 0);
  if (format === "currency" && Math.abs(value) < 1000)
    return formatters.formatCurrency(value, currency ?? undefined);
  if (format === "currency")
    return formatters.formatNumber(value, {
      style: "currency",
      currency: currency ?? "USD",
      currencyDisplay: currency === "USD" || currency === undefined ? "narrowSymbol" : "symbol",
      notation: "compact",
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  if (Math.abs(value) >= 1000)
    return `${formatters.formatNumber(value / 1000, { maximumFractionDigits: 1 })}k`;
  return formatters.formatNumber(value, { maximumFractionDigits: 1 });
}

function readPreference(key: string): string | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage.getItem(key);
  } catch {
    // Embedded previews and restricted browser contexts can deny storage access.
    return null;
  }
}

function writePreference(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Preferences remain usable for this session when persistence is unavailable.
  }
}
